// Shared SMS character/segment maths.
//
// Real SMS segmentation, not the naive length/160: GSM-7 messages fit 160
// characters in one segment but only 153 per segment once concatenated;
// any character outside GSM-7 (most emoji, smart quotes, some accents)
// switches the whole message to UCS-2, dropping the limits to 70/67.

// The GSM 03.38 basic character set + extension table
const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const GSM7_EXTENDED = '^{}\\[~]|€'

export const isGsm7 = (text) => {
  for (const ch of text || '') {
    if (!GSM7_BASIC.includes(ch) && !GSM7_EXTENDED.includes(ch)) return false
  }
  return true
}

// Extended-table characters cost two septets in GSM-7
const gsm7Length = (text) => {
  let len = 0
  for (const ch of text || '') {
    len += GSM7_EXTENDED.includes(ch) ? 2 : 1
  }
  return len
}

export const MAX_SEGMENTS = 3

// Every marketing text says how to stop them (PECR). The server adds this line
// to one that doesn't, before pricing and sending
// (lib/service/sms/smsSegments.withOptOut); this mirrors it so the preview and
// the count show the text as it will go. "STOP" in capitals, or "reply/text
// stop", counts; "don't stop by" doesn't.
export const OPT_OUT_LINE = 'Reply STOP to opt out'
export const hasOptOut = (text) => {
  const t = String(text || '')
  // An instruction, not just the word (server: smsSegments.hasOptOut)
  return /\b(reply|text|txt|send|message)\s+["'\u2018\u201C]?stop\b/i.test(t) ||
    /\bstop["'\u2019\u201D]?\s+(to|2)\s+(opt|end|unsub|stop|cancel|quit)/i.test(t) ||
    /\bopt[\s-]?out\b/i.test(t)
}
export const withOptOut = (text) => {
  const t = String(text || '')
  if (!t.trim() || hasOptOut(t)) return t
  return `${t.replace(/\s+$/, '')}\n${OPT_OUT_LINE}`
}

// Typographic punctuation as its plain GSM-7 twin. The server makes the same
// swap before a text is priced or sent (lib/service/sms/smsSegments.js
// gsmFriendly), so a curly apostrophe from autocorrect no longer makes a text
// UCS-2; the counts here match what's billed. Anything without a plain twin
// (emoji, most accents) is left as written.
const GSM_FRIENDLY = [
  [/[\u2018\u2019\u201A\u201B\u2032]/g, "'"],
  [/[\u201C\u201D\u201E\u201F\u2033]/g, '"'],
  [/[\u2013\u2014\u2212]/g, '-'],
  [/\u2026/g, '...'],
  [/[\u00A0\u2007\u202F\u2009\u200A]/g, ' '],
  [/[\u200B\u200C\u200D\u2060\uFEFF]/g, ''],
]
export const gsmFriendly = (text) => (typeof text === 'string' ? GSM_FRIENDLY.reduce((t, [re, to]) => t.replace(re, to), text) : text)

export const analyseSms = (raw) => {
  const text = gsmFriendly(raw)
  const gsm = isGsm7(text)
  const length = gsm ? gsm7Length(text) : (text || '').length
  const single = gsm ? 160 : 70
  const perSegment = gsm ? 153 : 67
  const segments = length === 0 ? 1 : (length <= single ? 1 : Math.ceil(length / perSegment))
  const maxChars = MAX_SEGMENTS === 1 ? single : perSegment * MAX_SEGMENTS
  return {
    gsm,
    length,
    segments,
    perSegment,
    single,
    maxChars,
    overLimit: length > maxChars,
    remainingInSegment: segments === 1 ? single - length : perSegment * segments - length,
  }
}

// Example values for the composer preview. Deliberately generic placeholders —
// a real first name (e.g. "Sophie") reads as a hardcoded person and confuses
// salons into thinking the blast will send that literally. FirstName can be
// overridden with a real sample recipient's name (see previewSubstitute).
export const SMS_MERGE_TAGS = [
  { tag: '{{FirstName}}', sample: 'Client X' },
  { tag: '{{SalonName}}', sample: 'Your Salon' },
  { tag: '{{BookingLink}}', sample: 'beu.app/book/yoursalon' },
]

// The salon's own booking page for a preview, from its gift card link
// (".../gift-cards"); null when it isn't known, so the sample shows instead
export const ownBookingLink = (links) => {
  const gift = links && links.GiftCardLink
  return gift && /\/gift-cards$/.test(gift) ? gift.replace(/\/gift-cards$/, '').replace(/^https?:\/\//, '') : null
}

// Analyse the message AS IT WILL SEND, not as typed: merge tags expand at
// send time, so a 144-char template with {{BookingLink}} in it is usually 2
// segments for a real client. Estimates use realistic expansions (a longer
// first name and a full booking URL) so the counter errs honest, not flattering.
const ESTIMATE_SAMPLES = {
  '{{FirstName}}': 'Alexandra',                                  // long-ish real name
  '{{SalonName}}': null,                                        // real salon name when known
  '{{BookingLink}}': 'https://app.beuapp.com/book/your-salon-name',
}
export const analyseSmsExpanded = (text, salonName) => {
  let expanded = text || ''
  Object.entries(ESTIMATE_SAMPLES).forEach(([tag, sample]) => {
    const value = tag === '{{SalonName}}' ? (salonName || 'Your Salon Name') : sample
    expanded = expanded.split(tag).join(value)
  })
  return { ...analyseSms(expanded), template: analyseSms(text || '') }
}

export const previewSubstitute = (text, salonName, sampleFirstName) => {
  return (text || '')
    .replaceAll('{{FirstName}}', sampleFirstName || 'Client X')
    .replaceAll('{{SalonName}}', salonName || 'Your Salon')
    .replaceAll('{{BookingLink}}', 'beu.app/book/yoursalon')
}
